import { Controller } from '@hotwired/stimulus'

// Create an embedded Google Map with:
// - Custom Retreaver styling
// - A red circle as the center of the map
// - A custom blue gradient popup with the address of the map
export default class extends Controller {
    static values = {
        apiKey: String,
    }

    connect() {
        const API_KEY = this.apiKeyValue
        const API_VERSION = 'weekly'

        // Import the Google Maps JavaScript API async
        // See: https://developers.google.com/maps/documentation/javascript/load-maps-js-api
        // This is the same version as in ^ but run through yarn format...
        function loadGoogleMapsApi(g) {
            let h
            let a
            let k
            const p = 'The Google Maps JavaScript API'
            const c = 'google'
            const l = 'importLibrary'
            const q = '__ib__'
            const m = document
            let b = window
            b = b[c] || (b[c] = {})
            const d = b.maps || (b.maps = {})
            const r = new Set()
            const e = new URLSearchParams()
            const u = () =>
                h ||
                (h = new Promise(async (f, n) => {
                    await (a = m.createElement('script'))
                    e.set('libraries', `${[...r]}`)
                    for (k in g) {
                        e.set(
                            k.replace(/[A-Z]/g, t => `_${t[0].toLowerCase()}`),
                            g[k],
                        )
                    }
                    e.set('callback', `${c}.maps.${q}`)
                    a.src = `https://maps.${c}apis.com/maps/api/js?${e}`
                    d[q] = f
                    a.onerror = () => (h = n(Error(`${p} could not load.`)))
                    a.nonce = m.querySelector('script[nonce]')?.nonce || ''
                    m.head.append(a)
                }))
            d[l]
                ? console.warn(`${p} only loads once. Ignoring:`, g)
                : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)))
        }

        loadGoogleMapsApi({
            key: API_KEY,
            v: API_VERSION,
        })

        this.loadMap()
    }

    async loadMap() {
        // This dynamically imports libraries on-demand
        const { Map, StyledMapType, Circle, OverlayView } = await google.maps.importLibrary('maps')

        const retreaverStyle = [
            {
                featureType: 'all',
                elementType: 'labels.text.fill',
                stylers: [
                    {
                        color: '#ffffff',
                    },
                    {
                        weight: '0.20',
                    },
                    {
                        lightness: '28',
                    },
                    {
                        saturation: '23',
                    },
                    {
                        visibility: 'off',
                    },
                ],
            },
            {
                featureType: 'all',
                elementType: 'labels.text.stroke',
                stylers: [
                    {
                        color: '#494949',
                    },
                    {
                        lightness: 13,
                    },
                    {
                        visibility: 'off',
                    },
                ],
            },
            {
                featureType: 'all',
                elementType: 'labels.icon',
                stylers: [
                    {
                        visibility: 'off',
                    },
                ],
            },
            {
                featureType: 'administrative',
                elementType: 'geometry.fill',
                stylers: [
                    {
                        color: '#000000',
                    },
                ],
            },
            {
                featureType: 'administrative',
                elementType: 'geometry.stroke',
                stylers: [
                    {
                        color: '#144b53',
                    },
                    {
                        lightness: 14,
                    },
                    {
                        weight: 1.4,
                    },
                ],
            },
            {
                featureType: 'administrative',
                elementType: 'labels',
                stylers: [
                    {
                        visibility: 'on',
                    },
                    {
                        color: '#00e5ff',
                    },
                ],
            },
            {
                featureType: 'administrative',
                elementType: 'labels.text.stroke',
                stylers: [
                    {
                        visibility: 'on',
                    },
                    {
                        color: '#000017',
                    },
                ],
            },
            {
                featureType: 'administrative.locality',
                elementType: 'labels',
                stylers: [
                    {
                        visibility: 'on',
                    },
                    {
                        color: '#00e5ff',
                    },
                ],
            },
            {
                featureType: 'administrative.locality',
                elementType: 'labels.text.stroke',
                stylers: [
                    {
                        visibility: 'on',
                    },
                    {
                        color: '#1e2336',
                    },
                ],
            },
            {
                featureType: 'administrative.neighborhood',
                elementType: 'labels.text',
                stylers: [
                    {
                        visibility: 'off',
                    },
                ],
            },
            {
                featureType: 'administrative.land_parcel',
                elementType: 'labels.text',
                stylers: [
                    {
                        visibility: 'off',
                    },
                ],
            },
            {
                featureType: 'landscape',
                elementType: 'all',
                stylers: [
                    {
                        color: '#1e2336',
                    },
                ],
            },
            {
                featureType: 'poi',
                elementType: 'geometry',
                stylers: [
                    {
                        color: '212537',
                    },
                    {
                        lightness: 5,
                    },
                    {
                        visibility: 'on',
                    },
                ],
            },
            {
                featureType: 'road.highway',
                elementType: 'geometry.fill',
                stylers: [
                    {
                        color: '#1e2336',
                    },
                    {
                        visibility: 'on',
                    },
                ],
            },
            {
                featureType: 'road.highway',
                elementType: 'geometry.stroke',
                stylers: [
                    {
                        color: '#00aabe',
                    },
                    {
                        lightness: 25,
                    },
                    {
                        visibility: 'on',
                    },
                ],
            },
            {
                featureType: 'road.arterial',
                elementType: 'geometry',
                stylers: [
                    {
                        visibility: 'on',
                    },
                ],
            },
            {
                featureType: 'road.arterial',
                elementType: 'geometry.fill',
                stylers: [
                    {
                        color: '#131620',
                    },
                    {
                        visibility: 'on',
                    },
                ],
            },
            {
                featureType: 'road.arterial',
                elementType: 'geometry.stroke',
                stylers: [
                    {
                        color: '#00aabe',
                    },
                    {
                        lightness: 16,
                    },
                    {
                        visibility: 'on',
                    },
                ],
            },
            {
                featureType: 'road.arterial',
                elementType: 'labels.text',
                stylers: [
                    {
                        visibility: 'on',
                    },
                ],
            },
            {
                featureType: 'road.arterial',
                elementType: 'labels.text.fill',
                stylers: [
                    {
                        color: '#00e5ff',
                    },
                ],
            },
            {
                featureType: 'road.arterial',
                elementType: 'labels.text.stroke',
                stylers: [
                    {
                        color: '#1e2336',
                    },
                ],
            },
            {
                featureType: 'road.local',
                elementType: 'geometry',
                stylers: [
                    {
                        color: '#141a31',
                    },
                    {
                        visibility: 'on',
                    },
                    {
                        lightness: '-22',
                    },
                ],
            },
            {
                featureType: 'road.local',
                elementType: 'labels.text',
                stylers: [
                    {
                        visibility: 'on',
                    },
                    {
                        color: '#575c72',
                    },
                ],
            },
            {
                featureType: 'road.local',
                elementType: 'labels.text.stroke',
                stylers: [
                    {
                        visibility: 'off',
                    },
                    {
                        hue: '#ff0000',
                    },
                ],
            },
            {
                featureType: 'transit',
                elementType: 'all',
                stylers: [
                    {
                        color: '#575c72',
                    },
                ],
            },
            {
                featureType: 'water',
                elementType: 'all',
                stylers: [
                    {
                        color: '#000017',
                    },
                ],
            },
        ]

        const center = { lat: 34.059386, lng: -118.280127 }
        const options = {
            zoom: 16.2,
            center,
            mapTypeControlOptions: {
                mapTypeIds: ['roadmap', 'greyscale'],
            },
            disableDefaultUI: true,
        }

        this.map = new Map(this.element, options)

        const mapType = new StyledMapType(retreaverStyle, { name: 'greyscale' })
        this.map.mapTypes.set('greyscale', mapType)
        this.map.setMapTypeId('greyscale')

        // Add red circle
        new Circle({
            strokeOpacity: 0,
            strokeWeight: 2,
            fillColor: '#FF4A23',
            fillOpacity: 1,
            map: this.map,
            center,
            radius: 20, // radius in meters
        })

        new Circle({
            strokeColor: '#FF4A23',
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: '#FF4A23',
            fillOpacity: 0,
            map: this.map,
            center,
            radius: 35, // radius in meters
        })

        // Create custom popup
        class Popup extends OverlayView {
            position
            containerDiv

            constructor(position, content) {
                super()

                this.position = position
                this.content = content
                this.div = null
            }

            onAdd() {
                this.div = document.getElementById('map-label')
                this.div.style.position = 'absolute'
                this.div.style.marginTop = '10px'

                const panes = this.getPanes()
                panes.overlayLayer.appendChild(this.div)
            }

            draw() {
                const overlayProjection = this.getProjection()
                const position = overlayProjection.fromLatLngToDivPixel(this.position)

                this.div.style.left = `${position.x - this.div.offsetWidth / 2}px`
                this.div.style.top = `${position.y - this.div.offsetHeight - 50}px`
            }

            onRemove() {
                if (this.div) {
                    this.div.parentNode.removeChild(this.div)
                    this.div = null
                }
            }
        }

        const popup = new Popup(
            new google.maps.LatLng(center.lat, center.lng),
            '2404 Wilshire BLVD, Unit 4F, Los Angeles, CA 90057',
        )
        popup.setMap(this.map)
    }
}
